import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import { Offer, offersApi, offerPath, formatDateRange, formatPrice } from '../../services/offersApi';
import { OfferShell, OfferState } from './OfferShell';

export default function OffersIndexPage() {
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useSEO({
    title: 'Special meal offers - BOQBOX',
    description: 'Event and conference meal plans, cooked fresh and delivered by BOQBOX.',
  });

  const load = () => {
    setError(null);
    setOffers(null);
    offersApi.list().then(setOffers).catch((e: Error) => setError(e.message));
  };
  useEffect(load, []);

  return (
    <OfferShell backTo="/" backLabel="BOQBOX home">
      <section className="mo-ix-hero">
        <div className="mo-ix-glow mo-ix-glow-a" aria-hidden="true" />
        <div className="mo-ix-glow mo-ix-glow-b" aria-hidden="true" />
        <div className="mo-container mo-ix-hero-inner">
          <div className="mo-ix-copy">
            <span className="mo-ix-eyebrow"><span aria-hidden="true">🍽️</span> Special meal offers</span>
            <h1>
              Good food for every <span className="mo-ix-accent">big day.</span>
            </h1>
            <p>
              Conferences, retreats and events — pick a meal plan, make it yours day by day, and we’ll bring it hot to your door or
              have it ready for pickup.
            </p>
            <div className="mo-ix-ctas">
              <a href="#offers" className="mo-btn mo-btn-primary" onClick={(e) => { e.preventDefault(); document.getElementById('offers')?.scrollIntoView({ behavior: 'smooth' }); }}>
                Browse offers
              </a>
              <a href="#how" className="mo-btn mo-ix-btn-glass" onClick={(e) => { e.preventDefault(); document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' }); }}>
                How it works
              </a>
            </div>
            <ul className="mo-ix-trust">
              <li><span aria-hidden="true">👩🏾‍🍳</span> Cooked fresh daily</li>
              <li><span aria-hidden="true">🛵</span> Delivery or pickup</li>
              <li><span aria-hidden="true">🔒</span> Secure Paystack checkout</li>
            </ul>
          </div>

          <div className="mo-ix-visual" aria-hidden="true">
            <div className="mo-ix-plate">
              <div className="mo-ix-plate-head">
                <span>Monday</span>
                <span className="mo-ix-pill">Your plan</span>
              </div>
              {[
                { emoji: '🍳', meal: 'Breakfast', time: '7:30am' },
                { emoji: '🍛', meal: 'Lunch', time: '1:00pm' },
                { emoji: '🍲', meal: 'Dinner', time: '7:00pm' },
              ].map((row) => (
                <div key={row.meal} className="mo-ix-plate-row">
                  <span className="mo-ix-plate-emoji">{row.emoji}</span>
                  <span className="mo-ix-plate-text"><strong>{row.meal}</strong><small>{row.time}</small></span>
                  <span className="mo-ix-check">✓</span>
                </div>
              ))}
              <div className="mo-ix-plate-foot"><span>Swap meals any time</span><span>＋ Add extras</span></div>
            </div>
            <div className="mo-ix-float mo-ix-float-a"><span>🛵</span> On its way</div>
            <div className="mo-ix-float mo-ix-float-b"><span>🧃</span> Drinks added</div>
          </div>
        </div>
        <svg className="mo-ix-curve" viewBox="0 0 1440 60" preserveAspectRatio="none" aria-hidden="true"><path d="M0 60V22C240 0 480 0 720 18s480 30 720 4v38z" /></svg>
      </section>

      <div className="mo-container">
        <ol className="mo-ix-steps" id="how">
          {[
            { n: '1', title: 'Pick an offer & plan', text: 'Choose the event and the plan that fits your stay.' },
            { n: '2', title: 'Make it yours', text: 'Swap meals, change portions and add drinks or extras for each day.' },
            { n: '3', title: 'We handle the rest', text: 'Pay securely, then we deliver every meal or have it ready for pickup.' },
          ].map((step) => (
            <li key={step.n} className="mo-card">
              <span className="mo-ix-step-n">{step.n}</span>
              <div><strong>{step.title}</strong><span>{step.text}</span></div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mo-container mo-section" id="offers">
        {error ? (
          <OfferState icon="⚠️" title="We couldn’t load offers" message={error} action={<button className="mo-btn mo-btn-primary" onClick={load}>Try again</button>} />
        ) : offers === null ? (
          <div className="mo-grid mo-grid-3">
            {[0, 1, 2].map((i) => <div key={i} className="mo-skeleton" style={{ height: 300 }} />)}
          </div>
        ) : offers.length === 0 ? (
          <OfferState icon="🍽️" title="No offers right now" message="New event meal offers will appear here. Check back soon." action={<Link className="mo-btn mo-btn-ghost" to="/">Back to BOQBOX</Link>} />
        ) : (
          <>
            <div className="mo-section-head">
              <div>
                <h2>Available offers</h2>
                <p>{offers.length} offer{offers.length === 1 ? '' : 's'} open for reservations</p>
              </div>
            </div>
            <div className="mo-grid mo-grid-3">
              {offers.map((offer) => {
                const soldOut = Boolean(offer.totalSlots) && (offer.remainingSlots ?? 0) <= 0;
                const dates = formatDateRange(offer.startDate, offer.endDate);
                return (
                  <Link key={offer._id} to={offerPath(offer.slug)} className="mo-card mo-offer-card">
                    <div className="mo-offer-cover">{offer.image ? <img src={offer.image} alt="" loading="lazy" /> : null}</div>
                    <div className="mo-offer-body">
                      <h3>{offer.name}</h3>
                      {offer.description ? <p>{offer.description.length > 120 ? `${offer.description.slice(0, 120)}…` : offer.description}</p> : null}
                      <div className="mo-offer-meta">
                        <span>{dates || (soldOut ? 'Sold out' : 'Open now')}</span>
                        <span>{soldOut ? 'Sold out' : `Delivery ${formatPrice(offer.deliveryFeePerMeal ?? 400)}/meal →`}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </OfferShell>
  );
}
